import { generateUUID } from '@/common/utils/commonUtils';
import {
  computed,
  nextTick,
  onDeactivated,
  onActivated,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  watch,
} from 'vue';

// 동일 teleport 타겟 Element에 동시에 주입되는 상황에서 "마지막 활성 인스턴스"만 DOM을 렌더링.
// selector 문자열이 아닌 실제 Element를 키로 사용해 다른 페이지에서 같은 selector를 써도 충돌하지 않음.
const activeNoDataTeleportByElement = reactive(new Map<Element, string>());

export interface Props {
  isVisible: boolean;
  targetClass?: string;
  targetElement?: string;
  targetSelector?: string;
}

export const setup = (props: Props) => {
  const onLoad = ref<boolean>(false);

  const teleportContentId = `no-data-table-body-${generateUUID()}`;

  const teleportTo = computed<string>(() => {
    if (props.targetSelector) return props.targetSelector;
    const target = props.targetElement ?? '.modern-grid-body-area';
    return `.${props.targetClass} ${target}`;
  });

  const showContentBase = computed<boolean>(() => props.isVisible && onLoad.value);

  const resolvedTarget = ref<Element | null>(null);

  const registerSelf = () => {
    if (!props.isVisible || !onLoad.value) return;
    const el = document.querySelector(teleportTo.value);
    if (!el) return;
    resolvedTarget.value = el;
    activeNoDataTeleportByElement.set(el, teleportContentId);
  };

  // 동일 Element를 타겟으로 하는 인스턴스 중 1개만 렌더링되도록 보장
  const showContent = computed<boolean>(() => {
    if (!showContentBase.value) return false;
    const el = resolvedTarget.value;
    if (!el) return false;
    return activeNoDataTeleportByElement.get(el) === teleportContentId;
  });

  const cleanup = () => {
    const el = resolvedTarget.value;
    if (el && activeNoDataTeleportByElement.get(el) === teleportContentId) {
      activeNoDataTeleportByElement.delete(el);
    }
  };

  onMounted(() => {
    onLoad.value = true;
    registerSelf();
  });

  // keep-alive로 캐시된 상태에서 다시 활성화될 때 onLoad를 복구해야 함
  onActivated(() => {
    onLoad.value = true;
    registerSelf();
  });
  // keep-alive로 deActivated 상태여도 텔레포트된 DOM은 남기지 않음
  onDeactivated(cleanup);
  onUnmounted(cleanup);

  // isVisible이 뒤늦게 true로 바뀌는 경우(mount 이후) 등록 후,
  // 테이블 기본 빈상태(is-empty)와 커스텀 no-data가 중복되지 않도록 is-empty 제거.
  // registerSelf() → nextTick 후 showContent 확인 순서를 하나의 watch로 보장.
  watch(
    () => props.isVisible,
    async (v) => {
      if (v) registerSelf();
      await nextTick();
      if (!v || !showContent.value || !props.targetClass) return;
      const element = document.querySelector(`.${props.targetClass}`);
      element?.querySelector('.is-empty')?.remove();
    },
    { immediate: true },
  );

  return {
    showContent,
    teleportTo,
    teleportContentId,
  };
};
